
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Response} from '../core/Response';
import {FieldModelResponse} from '../core/Response';

/**
 * PongResponse struct
 */
class PongResponse extends Response {
  /**
   * Initialize struct
   * @param {!Response=} parent
   * @param {!Date=} ping
   * @param {!Date=} pong
   * @constructor
   */
  constructor (parent = new Response(), ping = new Date(0), pong = new Date(0)) {
    super()
    super.copy(parent)
    this.Ping = ping
    this.Pong = pong
  }

  /**
   * Copy struct (shallow copy)
   * @this {!PongResponse}
   * @param {!PongResponse} other Other struct
   * @returns {!PongResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.Ping != null) {
      if (other.Ping instanceof Date) {
        this.Ping = new Date(other.Ping.getTime())
      } else {
        this.Ping = new Date(Math.round(other.Ping / 1000000))
      }
    } else {
      this.Ping = null
    }
    if (other.Pong != null) {
      if (other.Pong instanceof Date) {
        this.Pong = new Date(other.Pong.getTime())
      } else {
        this.Pong = new Date(Math.round(other.Pong / 1000000))
      }
    } else {
      this.Pong = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!PongResponse}
   * @returns {!PongResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new PongResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new PongResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!PongResponse}
   * @param {!PongResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof PongResponse)) {
      throw new TypeError('Instance of PongResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!PongResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      Ping: ((this.Ping != null) ? (this.Ping.getTime() * 1000000) : null),
      Pong: ((this.Pong != null) ? (this.Pong.getTime() * 1000000) : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return PongResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!PongResponse} other Object value
   * @returns {!PongResponse} Created struct
   */
  static fromObject (other) {
    return new PongResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!PongResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return PongResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!PongResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5002
  }
}

export { PongResponse };

/**
 * Fast Binary Encoding PongResponse field model
 */
class FieldModelPongResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelResponse(buffer, 4 + 4)
    this.Ping = new fbe.FieldModelTimestamp(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.Pong = new fbe.FieldModelTimestamp(buffer, this.Ping.fbeOffset + this.Ping.fbeSize)
  }

  /**
   * Get the core.Response field model
   * @this {!FieldModelPongResponse}
   * @returns {!FieldModelResponse} core.Response field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelPongResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelPongResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.Ping.fbeSize + this.Pong.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelPongResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.Ping.fbeExtra + this.Pong.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelPongResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelPongResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelPongResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5002
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelPongResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelPongResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Ping.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Ping.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Ping.fbeSize

    if ((fbeCurrentSize + this.Pong.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Pong.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Pong.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelPongResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelPongResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelPongResponse}
   * @param {!PongResponse} fbeValue Default value, defaults is new PongResponse()
   * @returns {!PongResponse} PongResponse value
   */
  get (fbeValue = new PongResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelPongResponse}
   * @param {!PongResponse} fbeValue PongResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Ping.fbeSize) <= fbeStructSize) {
      fbeValue.Ping = this.Ping.get()
    } else {
      fbeValue.Ping = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Ping.fbeSize

    if ((fbeCurrentSize + this.Pong.fbeSize) <= fbeStructSize) {
      fbeValue.Pong = this.Pong.get()
    } else {
      fbeValue.Pong = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Pong.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelPongResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelPongResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelPongResponse}
   * @param {!PongResponse} fbeValue PongResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelPongResponse}
   * @param {PongResponse} fbeValue PongResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.Ping.set(fbeValue.Ping)
    this.Pong.set(fbeValue.Pong)
  }
}

export { FieldModelPongResponse };

/**
 * Fast Binary Encoding PongResponse model
 */
class PongResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelPongResponse(this.buffer, 4)
  }

  /**
   * Get the PongResponse model
   * @this {!PongResponseModel}
   * @returns {!FieldModelPongResponse} model PongResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!PongResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!PongResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return PongResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!PongResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelPongResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!PongResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!PongResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!PongResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!PongResponseModel}
   * @param {!PongResponse} value PongResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!PongResponseModel}
   * @param {!PongResponse} value PongResponse value, defaults is new PongResponse()
   * @return {!object} Deserialized PongResponse value and its size
   */
  deserialize (value = new PongResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new PongResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new PongResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!PongResponseModel}
   * @param {!number} prev Previous PongResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { PongResponseModel };
